import { Commit, Dispatch } from 'vuex'
import { isApiError, AdminUserSummary, AdminSignupCode, CreateSignupCodesRequest, InviteSignupCodeRequest, AdminProvisionRequest, AdminMcpServerRequest, AdminMcpServerRequestStatus, BillingModelRow, AdminProvisioningStateResponse } from '@/types/api'
import { adminListUsers, adminSetOrToggleCanPublish, adminDeleteUser, adminListSignupCodes, adminCreateSignupCodes, adminInviteSignupCode, adminActivateSignupCode, adminDeactivateSignupCode, adminDeleteSignupCode, adminProvisionAccount, adminListMcpServerRequests, adminUpdateMcpServerRequest, adminGetPlatformUsageBilling, adminGetUserUsageBilling, adminGetUserProvisioningState } from '@/utils/api'

type MonthRange = { startISO: string; endISO: string; label: string }

function computeMonthRange(year: number, monthIndex0: number): MonthRange {
  const startMs = Date.UTC(year, monthIndex0, 1, 0, 0, 0, 0)
  const endMs = Date.UTC(year, monthIndex0 + 1, 1, 0, 0, 0, 0)
  const startISO = new Date(startMs).toISOString()
  const endISO = new Date(endMs).toISOString()
  const label = new Date(startMs).toLocaleString(undefined, { month: 'long', year: 'numeric' })
  return { startISO, endISO, label }
}

export interface AdminState {
  // Users paging/state
  users: AdminUserSummary[]
  loading: boolean
  error: string | null
  limit: number
  offset: number

  // Signup Codes paging/state
  codes: AdminSignupCode[]
  codesLoading: boolean
  codesError: string | null
  codesLimit: number
  codesOffset: number
  codesFilter: { active?: boolean; email?: string }

  // MCP Server Requests
  mcpRequests: AdminMcpServerRequest[]
  mcpRequestsLoading: boolean
  mcpRequestsError: string | null
  mcpRequestsTotal: number
  mcpRequestsLimit: number
  mcpRequestsOffset: number
  mcpRequestsFilter: { status?: 'pending' | 'approved' | 'rejected' | 'installed'; requestType?: 'platform_install' | 'external_removal'; q?: string }

  // Usage (platform + per-user)
  platformUsageSelectedMonth: MonthRange | null
  platformUsageBillingByModel: BillingModelRow[]
  platformUsageLoading: boolean
  platformUsageError: string | null

  userUsageDialogOpen: boolean
  userUsageUsername: string | null
  userUsageBillingByModel: BillingModelRow[]
  userUsageLoading: boolean
  userUsageError: string | null

  targetProvisioningState: AdminProvisioningStateResponse | null
  targetProvisioningStateLoading: boolean
  targetProvisioningStateError: string | null
}

const initialState: AdminState = {
  // Users
  users: [],
  loading: false,
  error: null,
  limit: 100,
  offset: 0,

  // Signup Codes
  codes: [],
  codesLoading: false,
  codesError: null,
  codesLimit: 50,
  codesOffset: 0,
  codesFilter: {},

  // MCP Server Requests
  mcpRequests: [],
  mcpRequestsLoading: false,
  mcpRequestsError: null,
  mcpRequestsTotal: 0,
  mcpRequestsLimit: 50,
  mcpRequestsOffset: 0,
  mcpRequestsFilter: {},

  // Usage
  platformUsageSelectedMonth: null,
  platformUsageBillingByModel: [],
  platformUsageLoading: false,
  platformUsageError: null,

  userUsageDialogOpen: false,
  userUsageUsername: null,
  userUsageBillingByModel: [],
  userUsageLoading: false,
  userUsageError: null,

  targetProvisioningState: null,
  targetProvisioningStateLoading: false,
  targetProvisioningStateError: null
}

export const admin = () => ({
  namespaced: true,
  state: <AdminState>{
    ...initialState,
    users: [],
    loading: false,
    error: null,
    limit: initialState.limit,
    offset: initialState.offset,
    mcpRequests: [],
    mcpRequestsLoading: false,
    mcpRequestsError: null,
    mcpRequestsTotal: 0,
    mcpRequestsLimit: initialState.mcpRequestsLimit,
    mcpRequestsOffset: initialState.mcpRequestsOffset,
    mcpRequestsFilter: initialState.mcpRequestsFilter,

    platformUsageSelectedMonth: initialState.platformUsageSelectedMonth,
    platformUsageBillingByModel: [],
    platformUsageLoading: false,
    platformUsageError: null,

    userUsageDialogOpen: false,
    userUsageUsername: null,
    userUsageBillingByModel: [],
    userUsageLoading: false,
    userUsageError: null,

    targetProvisioningState: null,
    targetProvisioningStateLoading: false,
    targetProvisioningStateError: null
  },
  mutations: {
    setAdminUsers(state: AdminState, users: AdminUserSummary[]) {
      state.users = users
    },
    setAdminLoading(state: AdminState, loading: boolean) {
      state.loading = loading
    },
    setAdminError(state: AdminState, error: string | null) {
      state.error = error
    },
    setPaging(state: AdminState, { limit, offset }: { limit: number; offset: number }) {
      state.limit = limit
      state.offset = offset
    },

    // Signup Codes mutations
    setAdminCodes(state: AdminState, codes: AdminSignupCode[]) {
      state.codes = codes
    },
    setAdminCodesLoading(state: AdminState, loading: boolean) {
      state.codesLoading = loading
    },
    setAdminCodesError(state: AdminState, error: string | null) {
      state.codesError = error
    },
    setAdminCodesPaging(state: AdminState, { limit, offset }: { limit: number; offset: number }) {
      state.codesLimit = limit
      state.codesOffset = offset
    },
    setAdminCodesFilter(state: AdminState, filter: { active?: boolean; email?: string }) {
      state.codesFilter = filter ?? {}
    },

    // MCP Server Requests mutations
    setMcpRequests(state: AdminState, { requests, total }: { requests: AdminMcpServerRequest[]; total: number }) {
      state.mcpRequests = requests
      state.mcpRequestsTotal = total
    },
    setMcpRequestsLoading(state: AdminState, loading: boolean) {
      state.mcpRequestsLoading = loading
    },
    setMcpRequestsError(state: AdminState, error: string | null) {
      state.mcpRequestsError = error
    },
    setMcpRequestsPaging(state: AdminState, { limit, offset }: { limit: number; offset: number }) {
      state.mcpRequestsLimit = limit
      state.mcpRequestsOffset = offset
    },
    setMcpRequestsFilter(state: AdminState, filter: AdminState['mcpRequestsFilter']) {
      state.mcpRequestsFilter = filter
    },

    // Usage mutations
    setPlatformUsageSelectedMonth(state: AdminState, month: MonthRange) {
      state.platformUsageSelectedMonth = month
    },
    setPlatformUsageBillingByModel(state: AdminState, rows: BillingModelRow[]) {
      state.platformUsageBillingByModel = rows
    },
    setPlatformUsageLoading(state: AdminState, loading: boolean) {
      state.platformUsageLoading = loading
    },
    setPlatformUsageError(state: AdminState, error: string | null) {
      state.platformUsageError = error
    },

    setUserUsageDialogOpen(state: AdminState, open: boolean) {
      state.userUsageDialogOpen = open
    },
    setUserUsageUsername(state: AdminState, username: string | null) {
      state.userUsageUsername = username
    },
    setUserUsageBillingByModel(state: AdminState, rows: BillingModelRow[]) {
      state.userUsageBillingByModel = rows
    },
    setUserUsageLoading(state: AdminState, loading: boolean) {
      state.userUsageLoading = loading
    },
    setUserUsageError(state: AdminState, error: string | null) {
      state.userUsageError = error
    },

    setTargetProvisioningState(state: AdminState, provisioningState: AdminProvisioningStateResponse | null) {
      state.targetProvisioningState = provisioningState
    },
    setTargetProvisioningStateLoading(state: AdminState, loading: boolean) {
      state.targetProvisioningStateLoading = loading
    },
    setTargetProvisioningStateError(state: AdminState, error: string | null) {
      state.targetProvisioningStateError = error
    }
  },
  actions: {
    async initPlatformUsageSelectedMonthToCurrent({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      const now = new Date()
      commit('setPlatformUsageSelectedMonth', computeMonthRange(now.getUTCFullYear(), now.getUTCMonth()))
      await dispatch('fetchPlatformUsageForSelectedMonth')
    },

    async changePlatformUsageSelectedMonth(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      { year, monthIndex0 }: { year: number; monthIndex0: number }
    ) {
      commit('setPlatformUsageSelectedMonth', computeMonthRange(year, monthIndex0))
      await dispatch('fetchPlatformUsageForSelectedMonth')
    },

    async fetchPlatformUsageForSelectedMonth({ state, commit, dispatch }: { state: AdminState; commit: Commit; dispatch: Dispatch }) {
      if (!state.platformUsageSelectedMonth) return
      commit('setPlatformUsageLoading', true)
      commit('setPlatformUsageError', null)

      const { startISO, endISO } = state.platformUsageSelectedMonth
      const response = await adminGetPlatformUsageBilling({ start: startISO, end: endISO, groupBy: 'model' })

      if (isApiError(response)) {
        console.error('Error fetching platform usage billing:', response.message)
        commit('setPlatformUsageError', response.message)
        dispatch('updateSnackbar', { text: 'Platform Usage Error: ' + response.message }, { root: true })
        dispatch('addLogEvent', { title: 'Platform Usage Fetch Error', content: response }, { root: true })
        commit('setPlatformUsageBillingByModel', [])
      } else if (response.success) {
        commit('setPlatformUsageBillingByModel', response.data as any)
      } else {
        const msg = 'Failed to fetch platform usage.'
        commit('setPlatformUsageError', msg)
        commit('setPlatformUsageBillingByModel', [])
      }

      commit('setPlatformUsageLoading', false)
    },

    async openUserUsageDialog(
      { commit, dispatch, state }: { commit: Commit; dispatch: Dispatch; state: AdminState },
      username: string
    ) {
      commit('setUserUsageDialogOpen', true)
      commit('setUserUsageUsername', username)
      commit('setUserUsageError', null)
      commit('setUserUsageBillingByModel', [])

      // Ensure we have a month selected (defaults to current UTC month)
      if (!state.platformUsageSelectedMonth) {
        const now = new Date()
        commit('setPlatformUsageSelectedMonth', computeMonthRange(now.getUTCFullYear(), now.getUTCMonth()))
      }

      await dispatch('fetchUserUsageForSelectedMonth')
    },

    closeUserUsageDialog({ commit }: { commit: Commit }, _?: any) {
      commit('setUserUsageDialogOpen', false)
      commit('setUserUsageUsername', null)
      commit('setUserUsageBillingByModel', [])
      commit('setUserUsageError', null)
    },

    async fetchUserUsageForSelectedMonth({ state, commit, dispatch }: { state: AdminState; commit: Commit; dispatch: Dispatch }) {
      if (!state.userUsageUsername) return
      if (!state.platformUsageSelectedMonth) return

      commit('setUserUsageLoading', true)
      commit('setUserUsageError', null)

      const { startISO, endISO } = state.platformUsageSelectedMonth
      const response = await adminGetUserUsageBilling({
        username: state.userUsageUsername,
        start: startISO,
        end: endISO,
        groupBy: 'model'
      })

      if (isApiError(response)) {
        console.error('Error fetching user usage billing:', response.message)
        commit('setUserUsageError', response.message)
        dispatch('updateSnackbar', { text: 'User Usage Error: ' + response.message }, { root: true })
        dispatch('addLogEvent', { title: 'User Usage Fetch Error', content: response }, { root: true })
        commit('setUserUsageBillingByModel', [])
      } else if (response.success) {
        commit('setUserUsageBillingByModel', response.data as any)
      } else {
        const msg = 'Failed to fetch user usage.'
        commit('setUserUsageError', msg)
        commit('setUserUsageBillingByModel', [])
      }

      commit('setUserUsageLoading', false)
    },

    async fetchAdminUsers(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch; state: AdminState }
    ) {
      commit('setAdminLoading', true)
      commit('setAdminError', null)

      const pageLimit = 500
      let offset = 0
      let allUsers: AdminUserSummary[] = []
      let hasMore = true

      while (hasMore) {
        const response = await adminListUsers(pageLimit, offset)

        if (isApiError(response)) {
          console.error('Error fetching admin users:', response.message)
          commit('setAdminError', response.message)
          dispatch('updateSnackbar', { text: `Admin Users Error: ${response.message}` }, { root: true })
          dispatch('addLogEvent', { title: 'Admin Users Fetch Error', content: response }, { root: true })
          commit('setAdminUsers', [])
          commit('setAdminLoading', false)
          return
        }

        allUsers = [...allUsers, ...response.users]
        hasMore = response.limit > 0 && response.users.length === response.limit
        if (hasMore) offset += response.limit
      }

      commit('setAdminUsers', allUsers)
      commit('setPaging', { limit: pageLimit, offset: 0 })
      dispatch(
        'addLogEvent',
        { title: 'Admin Users Fetched', content: { count: allUsers.length } },
        { root: true }
      )

      commit('setAdminLoading', false)
    },

    async toggleUserPublish(
      { commit, dispatch, state, rootGetters }: { commit: Commit; dispatch: Dispatch; state: AdminState; rootGetters: any },
      payload: { username: string; value?: boolean }
    ) {
      commit('setAdminLoading', true)
      commit('setAdminError', null)

      const response = await adminSetOrToggleCanPublish(payload.username, payload.value)

      if (isApiError(response)) {
        console.error('Error toggling publish permission:', response.message)
        commit('setAdminError', response.message)
        dispatch('updateSnackbar', { text: `Toggle Publish Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Toggle Publish Error', content: response }, { root: true })
      } else {
        dispatch('updateSnackbar', { text: `Publish permission ${response.canPublishAgents ? 'enabled' : 'disabled'} for ${response.username}` }, { root: true })
        dispatch('addLogEvent', { title: 'Toggle Publish Success', content: response }, { root: true })
        await dispatch('fetchAdminUsers')
      }

      commit('setAdminLoading', false)
    },

    async deleteUser(
      { commit, dispatch, state, rootGetters }: { commit: Commit; dispatch: Dispatch; state: AdminState; rootGetters: any },
      username: string
    ) {
      commit('setAdminLoading', true)
      commit('setAdminError', null)

      // Prevent self-deletion from the UI
      const currentUser = rootGetters.username as string
      if (currentUser && currentUser === username) {
        const msg = 'You cannot delete your own user.'
        commit('setAdminError', msg)
        dispatch('updateSnackbar', { text: msg }, { root: true })
        commit('setAdminLoading', false)
        return
      }

      const response = await adminDeleteUser(username)

      if (isApiError(response)) {
        console.error('Error deleting user:', response.message)
        commit('setAdminError', response.message)
        dispatch('updateSnackbar', { text: `Delete User Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Delete User Error', content: response }, { root: true })
      } else {
        dispatch('updateSnackbar', { text: `User '${username}' deleted.` }, { root: true })
        dispatch('addLogEvent', { title: 'Delete User Success', content: { username } }, { root: true })
        await dispatch('fetchAdminUsers')
      }

      commit('setAdminLoading', false)
    },

    // --- Signup Codes actions ---

    async fetchAdminCodes(
      { commit, dispatch, state }: { commit: Commit; dispatch: Dispatch; state: AdminState },
      params?: { active?: boolean; email?: string; limit?: number; offset?: number }
    ) {
      commit('setAdminCodesLoading', true)
      commit('setAdminCodesError', null)

      const limit = params?.limit ?? state.codesLimit
      const offset = params?.offset ?? state.codesOffset
      const active = params?.active ?? state.codesFilter.active
      const email = params?.email ?? state.codesFilter.email

      const response = await adminListSignupCodes({ active, email, limit, offset })
      if (isApiError(response)) {
        console.error('Error fetching signup codes:', response.message)
        commit('setAdminCodesError', response.message)
        dispatch('updateSnackbar', { text: `Signup Codes Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Signup Codes Fetch Error', content: response }, { root: true })
        commit('setAdminCodes', [])
      } else {
        commit('setAdminCodes', response.codes)
        commit('setAdminCodesPaging', { limit: response.limit, offset: response.offset })
        commit('setAdminCodesFilter', { active, email })
        dispatch(
          'addLogEvent',
          { title: 'Signup Codes Fetched', content: { count: response.codes.length, limit: response.limit, offset: response.offset, active, email } },
          { root: true }
        )
      }

      commit('setAdminCodesLoading', false)
    },

    async createAdminCodes(
      { commit, dispatch, state }: { commit: Commit; dispatch: Dispatch; state: AdminState },
      payload: CreateSignupCodesRequest
    ) {
      commit('setAdminCodesLoading', true)
      commit('setAdminCodesError', null)

      const response = await adminCreateSignupCodes(payload)
      if (isApiError(response)) {
        commit('setAdminCodesError', response.message)
        dispatch('updateSnackbar', { text: `Create Codes Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Create Codes Error', content: response }, { root: true })
      } else {
        dispatch('updateSnackbar', { text: `Created ${response.codes.length} code(s)` }, { root: true })
        dispatch('addLogEvent', { title: 'Create Codes Success', content: response.codes }, { root: true })
        await dispatch('fetchAdminCodes', { limit: state.codesLimit, offset: state.codesOffset, ...state.codesFilter })
      }

      commit('setAdminCodesLoading', false)
    },

    async inviteSignupCode(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      payload: InviteSignupCodeRequest
    ) {
      commit('setAdminCodesLoading', true)
      commit('setAdminCodesError', null)

      const response = await adminInviteSignupCode(payload)
      if (isApiError(response)) {
        commit('setAdminCodesError', response.message)
        dispatch('updateSnackbar', { text: `Invite Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Invite Code Error', content: response }, { root: true })
      } else {
        dispatch('updateSnackbar', { text: `Invite sent. Code: ${response.code}` }, { root: true })
        dispatch('addLogEvent', { title: 'Invite Code Success', content: response }, { root: true })
      }

      commit('setAdminCodesLoading', false)
    },

    async activateSignupCode(
      { commit, dispatch, state }: { commit: Commit; dispatch: Dispatch; state: AdminState },
      id: string
    ) {
      commit('setAdminCodesLoading', true)
      const response = await adminActivateSignupCode(id)
      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Activate Error: ${response.message}` }, { root: true })
        commit('setAdminCodesError', response.message)
      } else {
        dispatch('updateSnackbar', { text: `Code activated` }, { root: true })
        await dispatch('fetchAdminCodes', { limit: state.codesLimit, offset: state.codesOffset, ...state.codesFilter })
      }
      commit('setAdminCodesLoading', false)
    },

    async deactivateSignupCode(
      { commit, dispatch, state }: { commit: Commit; dispatch: Dispatch; state: AdminState },
      id: string
    ) {
      commit('setAdminCodesLoading', true)
      const response = await adminDeactivateSignupCode(id)
      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Deactivate Error: ${response.message}` }, { root: true })
        commit('setAdminCodesError', response.message)
      } else {
        dispatch('updateSnackbar', { text: `Code deactivated` }, { root: true })
        await dispatch('fetchAdminCodes', { limit: state.codesLimit, offset: state.codesOffset, ...state.codesFilter })
      }
      commit('setAdminCodesLoading', false)
    },

    async deleteSignupCode(
      { commit, dispatch, state }: { commit: Commit; dispatch: Dispatch; state: AdminState },
      id: string
    ) {
      commit('setAdminCodesLoading', true)
      const response = await adminDeleteSignupCode(id)
      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Delete Code Error: ${response.message}` }, { root: true })
        commit('setAdminCodesError', response.message)
      } else {
        dispatch('updateSnackbar', { text: `Code deleted` }, { root: true })
        await dispatch('fetchAdminCodes', { limit: state.codesLimit, offset: state.codesOffset, ...state.codesFilter })
      }
      commit('setAdminCodesLoading', false)
    },

    async provisionAccount(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      payload: AdminProvisionRequest
    ) {
      commit('setAdminLoading', true)
      commit('setAdminError', null)

      const response = await adminProvisionAccount(payload)

      if (isApiError(response)) {
        console.error('Error provisioning account:', response.message)
        commit('setAdminError', response.message)
        dispatch('updateSnackbar', { text: `Provision Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Account Provision Error', content: response }, { root: true })
        throw new Error(response.message)
      } else {
        dispatch('updateSnackbar', { text: `Account provisioned successfully for ${payload.userId}` }, { root: true })
        dispatch('addLogEvent', { title: 'Account Provisioned', content: { userId: payload.userId } }, { root: true })
      }

      commit('setAdminLoading', false)
    },

    async fetchTargetProvisioningState(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      username: string
    ) {
      if (!username || username.trim() === '') {
        commit('setTargetProvisioningState', null)
        commit('setTargetProvisioningStateError', null)
        commit('setTargetProvisioningStateLoading', false)
        return
      }

      commit('setTargetProvisioningStateLoading', true)
      commit('setTargetProvisioningStateError', null)

      const response = await adminGetUserProvisioningState(username)

      if (isApiError(response)) {
        console.error('Error fetching target provisioning state:', response.message)
        commit('setTargetProvisioningState', null)
        commit('setTargetProvisioningStateError', response.message)
        dispatch('updateSnackbar', { text: `Provisioning State Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Provisioning State Fetch Error', content: response }, { root: true })
      } else {
        commit('setTargetProvisioningState', response)
      }

      commit('setTargetProvisioningStateLoading', false)
    },

    // --- MCP Server Requests actions ---

    async fetchAdminMcpRequests(
      { commit, dispatch, state }: { commit: Commit; dispatch: Dispatch; state: AdminState },
      params?: { status?: AdminMcpServerRequestStatus; requestType?: 'platform_install' | 'external_removal'; q?: string; limit?: number; offset?: number }
    ) {
      commit('setMcpRequestsLoading', true)
      commit('setMcpRequestsError', null)

      const queryParams = {
        ...state.mcpRequestsFilter,
        limit: state.mcpRequestsLimit,
        offset: state.mcpRequestsOffset,
        ...params
      }

      const response = await adminListMcpServerRequests(queryParams)

      if (isApiError(response)) {
        console.error('Error fetching MCP requests:', response.message)
        commit('setMcpRequestsError', response.message)
        dispatch('updateSnackbar', { text: 'Failed to load MCP requests', color: 'error' }, { root: true })
        dispatch('addLogEvent', { title: 'MCP Requests Fetch Error', content: response }, { root: true })
        commit('setMcpRequests', { requests: [], total: 0 })
      } else {
        commit('setMcpRequests', { requests: response.requests, total: response.total })
        dispatch(
          'addLogEvent',
          { title: 'MCP Requests Fetched', content: { count: response.requests.length, total: response.total } },
          { root: true }
        )
      }

      commit('setMcpRequestsLoading', false)
    },

    async updateAdminMcpRequest(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      { id, status, reviewNotes }: { id: string; status: 'pending' | 'approved' | 'rejected'; reviewNotes?: string }
    ): Promise<boolean> {
      const response = await adminUpdateMcpServerRequest(id, { status, reviewNotes })

      if (isApiError(response)) {
        console.error('Error updating MCP request:', response.message)
        dispatch('updateSnackbar', { text: 'Failed to update MCP request', color: 'error' }, { root: true })
        dispatch('addLogEvent', { title: 'MCP Request Update Error', content: response }, { root: true })
        return false
      }

      dispatch('updateSnackbar', { text: 'MCP request updated' }, { root: true })
      dispatch('addLogEvent', { title: 'MCP Request Updated', content: { id, status } }, { root: true })
      await dispatch('fetchAdminMcpRequests')
      return true
    }
  },
  getters: {
    // Users
    adminUsers: (state: AdminState) => state.users,
    adminLoading: (state: AdminState) => state.loading,
    adminError: (state: AdminState) => state.error,
    adminPaging: (state: AdminState) => ({ limit: state.limit, offset: state.offset }),

    // Signup Codes
    adminCodes: (state: AdminState) => state.codes,
    adminCodesLoading: (state: AdminState) => state.codesLoading,
    adminCodesError: (state: AdminState) => state.codesError,
    adminCodesPaging: (state: AdminState) => ({ limit: state.codesLimit, offset: state.codesOffset }),
    adminCodesFilter: (state: AdminState) => state.codesFilter,

    // MCP Server Requests
    mcpRequests: (state: AdminState) => state.mcpRequests,
    mcpRequestsLoading: (state: AdminState) => state.mcpRequestsLoading,
    mcpRequestsError: (state: AdminState) => state.mcpRequestsError,
    mcpRequestsTotal: (state: AdminState) => state.mcpRequestsTotal,
    mcpRequestsLimit: (state: AdminState) => state.mcpRequestsLimit,
    mcpRequestsOffset: (state: AdminState) => state.mcpRequestsOffset,
    mcpRequestsFilter: (state: AdminState) => state.mcpRequestsFilter,

    platformUsageSelectedMonth: (state: AdminState) => state.platformUsageSelectedMonth,
    platformUsageBillingByModel: (state: AdminState) => state.platformUsageBillingByModel,
    platformUsageLoading: (state: AdminState) => state.platformUsageLoading,
    platformUsageError: (state: AdminState) => state.platformUsageError,

    userUsageDialogOpen: (state: AdminState) => state.userUsageDialogOpen,
    userUsageUsername: (state: AdminState) => state.userUsageUsername,
    userUsageBillingByModel: (state: AdminState) => state.userUsageBillingByModel,
    userUsageLoading: (state: AdminState) => state.userUsageLoading,
    userUsageError: (state: AdminState) => state.userUsageError,

    targetProvisioningState: (state: AdminState) => state.targetProvisioningState,
    targetProvisioningStateLoading: (state: AdminState) => state.targetProvisioningStateLoading,
    targetProvisioningStateError: (state: AdminState) => state.targetProvisioningStateError
  }
})
